/**
 * ATL-1445 stage 3, P6 — the LOCAL EFFECTS of one queued master write: what the device shows for it
 * before the server has it (plan `docs/technical/write-cutover-stage3-app-seam.md` §3).
 *
 * An outbox v2 entry carries `effects` (opaque to the store, `outboxEntry.ts`). This file is its
 * schema, the only reader of it (`parseLocalEffects`), and the display-JSON conversion the record
 * effects use. The writer builds the effects in its `planX` from the SAME code that builds today's
 * Firestore batch (`saleStockLines`, `dailyStatIncrementsForSales`, …), so the local view cannot drift
 * from what Firestore mode shows; `localView.ts` folds them over the server state.
 *
 * Three kinds, each one a surface of §3:
 *  - `stock`: per (product, store) DELTAS, in the order the writer's batch applies them. A composite
 *    sold at sale time is already expanded into its ingredients and pseudo lines are already skipped
 *    (the writer's own loop decides, see `saleStockLines`). Folded with Firestore's `increment`
 *    semantics: a non-numeric current value is REPLACED by the delta, exactly as the server transform
 *    does — so an offline sale of a product with no stock entry for the store shows `-q`, which is what
 *    Firestore latency compensation shows today and what the stock guard then refuses on (invariant 8).
 *  - `dailyStats`: today's totals for one store (`increments`, the same raw numbers
 *    `dailyStatIncrementsForSales(…, false, …)` returns), absolute field sets (`assign`: a shift's
 *    `shiftOpened`/`shiftClosed`, invariant 11) and field deletions (`remove`, P9b: a cancelled shift
 *    closing). `businessDay` (`YYYY-MM-DD`) is what the overlay matches
 *    against the server entry's day: the two read modes key the same day differently (`d-M-y-<store>`
 *    in Firestore, `YYYY-MM-DD` in Supabase), so neither id can be compared directly.
 *  - `records`: a row of a list surface (`sales`, `clients`: the operations list, the client search)
 *    or a product of the catalogue (`products`: pending-create presence, plan §9 risk 5). `create`,
 *    `patch` (fields merged over the current row) or `delete` (the row hidden).
 *
 * STRICT, like the entry: an unknown field, an unknown version or a non-finite number makes the WHOLE
 * effects value unreadable — it contributes nothing and is reported (`parseLocalEffects` returns why).
 * A newer bundle's effects that an older bundle cannot read must not be half-applied.
 */
import { findNonJson, type JsonObject, type JsonPrimitive, type JsonValue } from './outboxEntry'

export const LOCAL_EFFECTS_VERSION = 1 as const

/** One (product, store) stock delta, in batch order. `productId` is the products dict key. */
export type StockDeltaEffect = {
  productId: string
  storeId: string
  delta: number
}

/** Today's totals of one store. */
export type DailyStatEffect = {
  storeId: string
  /** The Firestore `dailyStats` document id the write addresses (`d-M-y-<store>`). */
  dailyStatId: string
  /** The business day it books to, `YYYY-MM-DD` (invariant 7: the device's checkout day). */
  businessDay: string
  /** Numeric increments, applied with Firestore `increment` semantics. */
  increments: { [field: string]: number }
  /** Absolute field assignments (shift status). Applied after `increments`. */
  assign: { [field: string]: JsonPrimitive }
  /**
   * Fields the write DELETES from the day's document (P9b: a cancelled shift closing deletes
   * `shiftClosed`, `shiftClosedTime` and the closing amounts — Firestore `FieldValue.delete()`).
   * Applied after `assign`. Optional so every effect written before P9b (none carries it) reads as
   * "removes nothing"; an older bundle that meets one refuses the whole effects value (STRICT, above).
   */
  remove?: string[]
}

export const RECORD_COLLECTIONS = ['sales', 'products', 'clients'] as const
export type RecordCollection = (typeof RECORD_COLLECTIONS)[number]
export const RECORD_OPS = ['create', 'patch', 'delete'] as const
export type RecordOp = (typeof RECORD_OPS)[number]

/** A row of a list surface, or a product of the catalogue. */
export type RecordEffect = {
  collection: RecordCollection
  /** The document's Firestore id: the list key and the de-duplication key. */
  id: string
  op: RecordOp
  /** The row's store, for the list's store filter; `null` when the record has none. */
  store: string | null
  /** The row's date as an ISO instant, for the list's range filter and section; `null` when none. */
  date: string | null
  /** The row (`create`) or the changed fields (`patch`); `{}` for `delete`. Display JSON. */
  fields: JsonObject
}

export type LocalEffects = {
  v: typeof LOCAL_EFFECTS_VERSION
  stock: StockDeltaEffect[]
  dailyStats: DailyStatEffect[]
  records: RecordEffect[]
}

/** Compile-time proof that effects can be persisted as an entry's `effects` unchanged. */
type AssertJson<T extends JsonValue> = T
export type LocalEffectsAreJson = AssertJson<LocalEffects>

export const EMPTY_LOCAL_EFFECTS: Readonly<LocalEffects> = Object.freeze({
  v: LOCAL_EFFECTS_VERSION,
  stock: [],
  dailyStats: [],
  records: [],
})

// ============================================================================
// Reading
// ============================================================================

export type ParsedLocalEffects = { ok: true; effects: LocalEffects | null } | { ok: false; reason: string }

const EFFECTS_FIELDS: ReadonlySet<string> = new Set(['v', 'stock', 'dailyStats', 'records'])
const STOCK_FIELDS: ReadonlySet<string> = new Set(['productId', 'storeId', 'delta'])
const DAILY_STAT_FIELDS: ReadonlySet<string> = new Set(['storeId', 'dailyStatId', 'businessDay', 'increments', 'assign', 'remove'])
const RECORD_FIELDS: ReadonlySet<string> = new Set(['collection', 'id', 'op', 'store', 'date', 'fields'])

const BUSINESS_DAY = /^\d{4}-\d{2}-\d{2}$/

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function unknownKey(value: Record<string, unknown>, allowed: ReadonlySet<string>): string | null {
  for (const key of Object.keys(value)) if (!allowed.has(key)) return key
  return null
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
}

function stockProblem(value: unknown, path: string): string | null {
  if (!isObject(value)) return `${path}: not an object`
  const extra = unknownKey(value, STOCK_FIELDS)
  if (extra !== null) return `${path}.${extra}: unknown field`
  if (!nonEmptyString(value.productId)) return `${path}.productId: not a non-empty string`
  if (!nonEmptyString(value.storeId)) return `${path}.storeId: not a non-empty string`
  if (typeof value.delta !== 'number' || !Number.isFinite(value.delta)) return `${path}.delta: not a finite number`
  return null
}

function dailyStatProblem(value: unknown, path: string): string | null {
  if (!isObject(value)) return `${path}: not an object`
  const extra = unknownKey(value, DAILY_STAT_FIELDS)
  if (extra !== null) return `${path}.${extra}: unknown field`
  if (!nonEmptyString(value.storeId)) return `${path}.storeId: not a non-empty string`
  if (!nonEmptyString(value.dailyStatId)) return `${path}.dailyStatId: not a non-empty string`
  if (typeof value.businessDay !== 'string' || !BUSINESS_DAY.test(value.businessDay)) return `${path}.businessDay: not YYYY-MM-DD`
  if (!isObject(value.increments)) return `${path}.increments: not an object`
  for (const [field, n] of Object.entries(value.increments)) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return `${path}.increments.${field}: not a finite number`
  }
  if (!isObject(value.assign)) return `${path}.assign: not an object`
  for (const [field, v] of Object.entries(value.assign)) {
    if (!(v === null || typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)))) {
      return `${path}.assign.${field}: not a JSON primitive`
    }
  }
  if (value.remove !== undefined) {
    if (!Array.isArray(value.remove)) return `${path}.remove: not an array`
    for (let i = 0; i < value.remove.length; i++) {
      if (!nonEmptyString(value.remove[i])) return `${path}.remove[${i}]: not a non-empty string`
    }
  }
  return null
}

function recordProblem(value: unknown, path: string): string | null {
  if (!isObject(value)) return `${path}: not an object`
  const extra = unknownKey(value, RECORD_FIELDS)
  if (extra !== null) return `${path}.${extra}: unknown field`
  if (!oneOf(RECORD_COLLECTIONS, value.collection)) return `${path}.collection: unknown collection`
  if (!nonEmptyString(value.id)) return `${path}.id: not a non-empty string`
  if (!oneOf(RECORD_OPS, value.op)) return `${path}.op: unknown op`
  if (value.store !== null && !nonEmptyString(value.store)) return `${path}.store: not a non-empty string or null`
  if (value.date !== null && !(typeof value.date === 'string' && Number.isFinite(Date.parse(value.date)))) {
    return `${path}.date: not an ISO instant or null`
  }
  if (!isObject(value.fields)) return `${path}.fields: not an object`
  return findNonJson(value.fields, `${path}.fields`)
}

/**
 * The only way stored `effects` become `LocalEffects`. `null` is "this write has no local effects"
 * (ok). Anything else that does not match the schema EXACTLY is refused whole, with the reason.
 */
export function parseLocalEffects(value: JsonValue | null | undefined): ParsedLocalEffects {
  if (value === null || value === undefined) return { ok: true, effects: null }
  if (!isObject(value)) return { ok: false, reason: 'effects: not an object' }
  const extra = unknownKey(value, EFFECTS_FIELDS)
  if (extra !== null) return { ok: false, reason: `effects.${extra}: unknown field (a new field requires a new version)` }
  if (value.v !== LOCAL_EFFECTS_VERSION) return { ok: false, reason: `effects.v: expected ${LOCAL_EFFECTS_VERSION}` }
  const lists: Array<[string, unknown, (v: unknown, p: string) => string | null]> = [
    ['stock', value.stock, stockProblem],
    ['dailyStats', value.dailyStats, dailyStatProblem],
    ['records', value.records, recordProblem],
  ]
  for (const [name, list, problem] of lists) {
    if (!Array.isArray(list)) return { ok: false, reason: `effects.${name}: not an array` }
    for (let i = 0; i < list.length; i++) {
      const found = problem(list[i], `effects.${name}[${i}]`)
      if (found) return { ok: false, reason: found }
    }
  }
  return { ok: true, effects: value as unknown as LocalEffects }
}

/** `true` when the effects change nothing on any surface. */
export function effectsAreEmpty(effects: LocalEffects | null): boolean {
  return effects === null || (effects.stock.length === 0 && effects.dailyStats.length === 0 && effects.records.length === 0)
}

// ============================================================================
// Building
// ============================================================================

/**
 * `YYYY-MM-DD` of a Firestore `dailyStats` id (`d-M-y-<store>`, `getDailyStatID`), or `null` when it is
 * not one. The id IS the booking day (invariant 7: computed from the checkout time at checkout).
 */
export function businessDayOfDailyStatId(dailyStatId: unknown): string | null {
  if (typeof dailyStatId !== 'string') return null
  const match = /^(\d{1,2})-(\d{1,2})-(\d{4})-./.exec(dailyStatId)
  if (!match) return null
  const [, d, m, y] = match
  const day = Number(d)
  const month = Number(m)
  if (day < 1 || day > 31 || month < 1 || month > 12) return null
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * The display JSON of a record: what a list renders, as plain JSON the outbox can persist.
 *  - A `Date` or a Firestore Timestamp (`toDate()`) becomes its ISO instant.
 *  - A non-finite number becomes `null` (a display value; the REQUEST is never built from this).
 *  - `undefined`, functions, symbols and non-plain objects (document references, class instances)
 *    are dropped: a list never renders them, and the outbox refuses them.
 * Display only: never used to build a request or a Firestore payload.
 */
export function toDisplayJson(value: unknown, depth = 0): JsonValue | undefined {
  if (depth > 32) return undefined
  if (value === null) return null
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return value
    case 'number':
      return Number.isFinite(value) ? value : null
    case 'object':
      break
    default:
      return undefined
  }
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString() : null
  const maybeTimestamp = value as { toDate?: unknown }
  if (typeof maybeTimestamp.toDate === 'function') {
    const date: unknown = (maybeTimestamp.toDate as () => unknown).call(value)
    return date instanceof Date && Number.isFinite(date.getTime()) ? date.toISOString() : null
  }
  if (Array.isArray(value)) {
    const out: JsonValue[] = []
    for (const item of value) {
      const converted = toDisplayJson(item, depth + 1)
      out.push(converted === undefined ? null : converted)
    }
    return out
  }
  const proto: unknown = Object.getPrototypeOf(value)
  if (!(proto === null || Object.getPrototypeOf(proto) === null)) return undefined
  const out: JsonObject = {}
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const converted = toDisplayJson(child, depth + 1)
    if (converted !== undefined) out[key] = converted
  }
  return out
}

/** `toDisplayJson` of an object, as an object (`{}` when the input is not a plain object). */
export function toDisplayObject(value: unknown): JsonObject {
  const converted = toDisplayJson(value)
  return converted !== null && typeof converted === 'object' && !Array.isArray(converted) ? converted : {}
}

/**
 * The raw-number increments of a `dailyStatIncrements…(…, forFirebase = false, …)` result. A value that
 * is not a number would mean the increment function returned a Firestore transform: a programming
 * error, thrown — never a value silently dropped from the totals.
 */
export function rawIncrements(increments: object): { [field: string]: number } {
  const out: { [field: string]: number } = {}
  for (const [field, value] of Object.entries(increments)) {
    if (typeof value !== 'number') throw new Error(`local effects: increment ${field} is not a raw number`)
    out[field] = value
  }
  return out
}

/** Effects a writer built that this module would refuse to read back (`assertLocalEffects`). */
export class LocalEffectsInvalidError extends Error {
  constructor(public readonly reason: string) {
    super(`local effects refused: ${reason}`)
    this.name = 'LocalEffectsInvalidError'
  }
}

/**
 * The effects a writer built, checked with the reader's own rules — at PLAN time, before the cart is
 * cleared. A value `parseLocalEffects` would refuse (a NaN quantity's delta, a non-finite total) would
 * otherwise be persisted and then silently shown as nothing (reviewer N5); refused here, the write fails
 * where the cashier can still see it.
 */
export function assertLocalEffects(effects: LocalEffects): LocalEffects {
  const parsed = parseLocalEffects(effects)
  if (!parsed.ok) throw new LocalEffectsInvalidError(parsed.reason)
  return effects
}

/** An ISO instant for a record's date (Date, Timestamp, ISO string or epoch ms), or `null`. */
export function isoInstantOf(value: unknown): string | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString() : null
  if (typeof value === 'number') return Number.isFinite(value) ? new Date(value).toISOString() : null
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null
  }
  if (value !== null && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return isoInstantOf((value as { toDate: () => unknown }).toDate())
  }
  return null
}
